// Marketing › Funis (lançamentos e os outros tipos de funil; BFF /api/marketing).
import { api } from './client';

export type TipoLancamento = 'pago' | 'gratuito' | 'front_end' | 'atracao' | 'depoimentos' | 'captacao_grupo' | 'live_estudos' | 'outro';
export type StatusLancamento = 'planejamento' | 'captacao' | 'vendas' | 'encerrado';
export type TipoItem = 'marco' | 'live' | 'email' | 'api' | 'grupo' | 'criativo' | 'tarefa';
export type StatusItem = 'planejado' | 'pronto' | 'enviado';

export type Lancamento = {
  id: string;
  nome: string;
  tipo: TipoLancamento;
  concurso: string | null;
  data_d0: string;
  tipo_calendario: string;
  cor: string | null;
  status: StatusLancamento;
  narrativa: string | null;
  calculadora: Record<string, unknown>;
  produtos: string[];
  obs: string | null;
  /** migration 56 (Funis); sem ela, vêm vazios */
  links?: Record<string, string>;
  acoes_trafego?: string[];
  regua?: Record<string, unknown>;
  criado_por: string | null;
  created_at: string;
  updated_at: string;
};
export type LancamentoResumo = Lancamento & { itens_total: number; itens_enviados: number; inicio: string; fim: string };

export type Item = {
  id: string;
  lancamento_id: string;
  tipo: TipoItem;
  titulo: string;
  offset_dias: number;
  data: string;
  hora: string | null;
  hora_fim: string | null;
  canal: string | null;
  conteudo: string | null;
  status: StatusItem;
  link: string | null;
  no_calendario: boolean;
  ordem: number;
  enviado_em: string | null;
  /** migration 58 */
  responsavel?: string | null;
};
export type ItemDados = Partial<Pick<Item, 'tipo' | 'titulo' | 'offset_dias' | 'hora' | 'hora_fim' | 'canal' | 'conteudo' | 'status' | 'link' | 'no_calendario' | 'responsavel'>>;
export type LancamentoDados = Partial<Pick<Lancamento, 'nome' | 'tipo' | 'concurso' | 'data_d0' | 'tipo_calendario' | 'cor' | 'status' | 'narrativa' | 'calculadora' | 'produtos' | 'obs' | 'links' | 'acoes_trafego' | 'regua'>>;

export type Modelo = { id: string; nome: string; tipo: TipoLancamento; descricao: string; qtd_itens: number };
export type Acompanhamento = {
  inicio: string;
  fim: string;
  dias: { dia: string; vendas: number; faturamento: number; leads: number }[];
  totais: { vendas: number; faturamento: number; leads: number };
  produtos_encontrados: { nome: string; qtd: number }[];
  obs: string[];
};
export type ChatAgente = { role: 'user' | 'assistant'; content: string };

/** Atividades do dia: itens dos funis no dia, tarefas avulsas e ações da reunião de tráfego. */
export type ItemDoDia = Item & { funil: string; concurso: string | null };
export type TarefaMkt = { setor?: string | null; id: string; dia: string; hora: string | null; titulo: string; descricao: string | null; responsavel: string | null; prazo: string | null; status: 'a_fazer' | 'feito'; feito_em: string | null; feito_por: string | null; lancamento_id: string | null; atrasada: boolean };
export type AcaoTrafegoDia = { id: string; descricao: string; responsavel: string | null; prazo: string | null; status: string; resultado: string | null };
export type AtividadesDia = { dia: string; itens: ItemDoDia[]; tarefas: TarefaMkt[]; trafego: AcaoTrafegoDia[]; avisos: string[] };
export type MinhasAtividades = { nome: string; hoje: number; proximos: number; atrasadas: number; itens: { id: string; dia: string; titulo: string; origem: 'tarefa' | 'funil' | 'trafego'; atrasada: boolean }[] };
export type DadosTarefa = { setor?: string | null; dia: string; titulo: string; hora?: string | null; descricao?: string | null; responsavel?: string | null; prazo?: string | null; lancamento_id?: string | null };

/** Grupos (SendFlow) e automações de API (Unnichat) ligados ao funil, com o último retrato coletado. */
export type SerieSF = { total: number; dias: Record<string, number> };
export type CampanhaGrupos = { id: string; nome: string; coletado_em: string; erro?: string; grupos: { id: string; nome: string; link: string | null; participantes: number; admins?: number; cheio: boolean; suspenso: boolean }[]; cliques?: SerieSF; entradas?: SerieSF; saidas?: SerieSF };
export type NumerosApi = { enviados: number; entregues: number; lidos: number; cliques_unicos: number };
export type AutomacaoApi = { id: string; nome: string; coletado_em: string; enviados: number; entregues: number; lidos: number; falhas: number; cliques: number; cliques_unicos: number; historico?: Record<string, NumerosApi> };
/** Painel dos grupos (Marketing › Grupos ao vivo): campanhas escolhidas com rótulo e bloco, e as fotos de cada coleta
 *  (em, p = pessoas, e/s/c = totais acumulados de entradas, saídas e cliques). */
export type FotoSF = { em: string; p: number; e: number; s: number; c: number };
export type ItemPainel = { id: string; rotulo: string; bloco: string };
export type ConfigPainel = { itens: ItemPainel[]; alvo: string | null; meta: number | null };
export type CampanhaPainel = CampanhaGrupos & ItemPainel & { fotos: FotoSF[] };
export type PainelGrupos = { config: ConfigPainel; campanhas: CampanhaPainel[] };
export type IntegracoesFunil = { ligacoes: { sendflow: string[]; unnichat: string[] }; campanhas: CampanhaGrupos[]; automacoes: AutomacaoApi[] };
export type OpcoesIntegracao = { campanhas: { id: string; nome: string; arquivada: boolean }[]; automacoes: { id: string; nome: string; ativa: boolean }[]; avisos: string[] };

/** Revisão do briefing pela IA antes de gerar: o que entendeu, correções, o que falta, perguntas e a versão corrigida. */
export type RevisaoBriefing = { entendimento: string; correcoes: { trecho: string; problema: string; sugestao: string }[]; faltando: string[]; perguntas: string[]; briefing_corrigido: string };
/** Ajuste pedido em texto livre: o que muda (briefing, plano e quais peças). */
export type PlanoAjuste = { resumo: string; briefing_novo: string | null; reescrever_plano: boolean; itens: { id: string; titulo: string; motivo: string }[] };

export const marketing = {
  modelos: () => api.get<Modelo[]>('/api/marketing/modelos'),
  produtos: () => api.get<{ nome: string; vendas: number }[]>('/api/marketing/produtos'),
  listar: () => api.get<LancamentoResumo[]>('/api/marketing/lancamentos'),
  obter: (id: string) => api.get<{ lancamento: Lancamento; itens: Item[] }>(`/api/marketing/lancamentos/${id}`),
  criar: (dados: LancamentoDados & { nome: string; data_d0: string; modelo?: string }) => api.post<{ id: string }>('/api/marketing/lancamentos', dados),
  atualizar: (id: string, patch: LancamentoDados) => api.patch<{ lancamento: Lancamento; itens: Item[] }>(`/api/marketing/lancamentos/${id}`, patch),
  excluir: (id: string) => api.del<{ ok: true }>(`/api/marketing/lancamentos/${id}`),
  duplicar: (id: string, nome: string, data_d0: string) => api.post<{ id: string }>(`/api/marketing/lancamentos/${id}/duplicar`, { nome, data_d0 }),
  aplicarModelo: (id: string, modelo: string) => api.post<{ itens: number }>(`/api/marketing/lancamentos/${id}/modelo`, { modelo }),
  acompanhamento: (id: string) => api.get<Acompanhamento>(`/api/marketing/lancamentos/${id}/acompanhamento`),
  briefing: (id: string) => api.get<{ texto: string }>(`/api/marketing/lancamentos/${id}/briefing`),
  salvarBriefing: (id: string, texto: string) => api.put<{ texto: string }>(`/api/marketing/lancamentos/${id}/briefing`, { texto }),
  revisarBriefing: (id: string, texto: string) => api.post<RevisaoBriefing>(`/api/marketing/lancamentos/${id}/briefing/revisar`, { texto }),
  planejarAjuste: (id: string, pedido: string) => api.post<PlanoAjuste>(`/api/marketing/lancamentos/${id}/ajuste`, { pedido }),
  escrever: (id: string, alvo: 'narrativa' | 'item', itemId?: string, pedido?: string) =>
    api.post<{ texto: string }>(`/api/marketing/lancamentos/${id}/escrever`, { alvo, ...(itemId ? { item_id: itemId } : {}), ...(pedido ? { pedido } : {}) }),
  criarItem: (id: string, dados: ItemDados & { tipo: TipoItem; titulo: string; offset_dias: number }) => api.post<{ ok: true }>(`/api/marketing/lancamentos/${id}/itens`, dados),
  atualizarItem: (itemId: string, patch: ItemDados) => api.patch<{ ok: true }>(`/api/marketing/itens/${itemId}`, patch),
  excluirItem: (itemId: string) => api.del<{ ok: true }>(`/api/marketing/itens/${itemId}`),
  // Início › Gestão de atividades (toda a equipe)
  atividades: (dia: string) => api.get<AtividadesDia>(`/api/gestao-atividades?dia=${dia}`),
  usuarios: () => api.get<{ id: string; nome: string }[]>('/api/gestao-atividades/usuarios'),
  minhas: () => api.get<MinhasAtividades>('/api/gestao-atividades/minhas'),
  tarefasNoPeriodo: (de: string, ate: string) => api.get<TarefaMkt[]>(`/api/gestao-atividades/periodo?de=${de}&ate=${ate}`),
  criarTarefa: (t: DadosTarefa) => api.post<{ ok: true }>('/api/gestao-atividades/tarefas', t),
  atualizarTarefa: (id: string, patch: Partial<DadosTarefa> & { status?: 'a_fazer' | 'feito' }) => api.patch<{ ok: true }>(`/api/gestao-atividades/tarefas/${id}`, patch),
  excluirTarefa: (id: string) => api.del<{ ok: true }>(`/api/gestao-atividades/tarefas/${id}`),
  opcoesIntegracao: () => api.get<OpcoesIntegracao>('/api/marketing/integracoes/opcoes'),
  integracoes: (id: string) => api.get<IntegracoesFunil>(`/api/marketing/lancamentos/${id}/integracoes`),
  ligarIntegracoes: (id: string, l: { sendflow: string[]; unnichat: string[] }) => api.put<IntegracoesFunil>(`/api/marketing/lancamentos/${id}/integracoes`, l),
  painelGrupos: () => api.get<PainelGrupos>('/api/marketing/grupos-painel'),
  salvarPainelGrupos: (c: ConfigPainel) => api.put<PainelGrupos>('/api/marketing/grupos-painel', c),
  coletarPainelGrupos: () => api.post<PainelGrupos & { erros: string[] }>('/api/marketing/grupos-painel/coletar', {}),
  coletarIntegracoes: (id: string) => api.post<IntegracoesFunil & { erros: string[] }>(`/api/marketing/lancamentos/${id}/integracoes/coletar`, {}),
  agente: (historico: ChatAgente[], lancamentoId?: string) =>
    api.post<{ resposta: string; consultas: string[] }>('/api/marketing/agente', { historico, ...(lancamentoId ? { lancamento_id: lancamentoId } : {}) }),
};
